import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Text, useColorModeValue, useToken } from '@bonusly/design-system';

import type { BizyMatrixNode } from '../../../../lib/bizy/types/nodes';
import { HighchartsExportBar } from '../lib/highcharts-export-bar';
// Importing highcharts-shared registers the heatmap + export modules (side effect).
import { resolveCssColor } from '../lib/highcharts-shared';
import { TruncationCaption } from '../lib/truncation-caption';
import { useEvidenceDrill } from '../lib/use-evidence-drill';
import type { BizyNodeProps } from './node-props';

type MatrixPayload = BizyMatrixNode['payload'];
type ValueFormat = MatrixPayload['valueFormat'];

// Heat gradient stops as literal hex (the colorAxis interpolates between them,
// and CSS-var colors can't be interpolated). MIN end is mode-aware.
const HEAT_MIN_LIGHT = '#eaf2fd';
const HEAT_MIN_DARK = '#1f2d44';
const HEAT_MAX = '#5b8def';

export function formatValue(value: number, format: ValueFormat): string {
	switch (format) {
		case 'percent':
			return `${value}%`;
		case 'currency':
			return `$${value.toLocaleString()}`;
		default:
			return value.toLocaleString();
	}
}

// Key a (row, column) pair with a NUL delimiter that can't appear in a label,
// so "A B"×"C" and "A"×"B C" don't collide into the same key.
const cellKey = (row: string, col: string) => `${row}\u0000${col}`;

interface BuildHeatmapArgs {
	rowKeys: string[];
	colKeys: string[];
	valueAt: (row: string, col: string) => number | undefined;
	fillZeros: boolean;
	valueFormat: ValueFormat;
	rowLabel?: string;
	columnLabel?: string;
	valueLabel?: string;
	fallbackLabels?: { row: string; column: string; value: string };
	axisColor: string;
	gridColor: string;
	labelColor: string;
	minColor?: string;
	maxColor?: string;
	onCellClick?: (label: string) => void;
}

export function buildHeatmapOptions({
	rowKeys,
	colKeys,
	valueAt,
	fillZeros,
	valueFormat,
	rowLabel,
	columnLabel,
	valueLabel,
	fallbackLabels = { row: 'Row', column: 'Column', value: 'Value' },
	axisColor,
	gridColor,
	labelColor,
	minColor = HEAT_MIN_LIGHT,
	maxColor = HEAT_MAX,
	onCellClick,
}: BuildHeatmapArgs): Highcharts.Options {
	const data: Highcharts.PointOptionsObject[] = [];
	rowKeys.forEach((row, y) =>
		colKeys.forEach((col, x) => {
			const raw = valueAt(row, col);
			data.push({ x, y, value: raw ?? (fillZeros ? 0 : null) });
		})
	);
	const maxValue = Math.max(0, ...data.map((d) => d.value ?? 0)) || 1;
	const axisLabelStyle = { color: axisColor, fontSize: '11px' };

	return {
		chart: { type: 'heatmap', backgroundColor: 'transparent', spacing: [8, 8, 8, 8] },
		title: { text: '' },
		credits: { enabled: false },
		exporting: {
			enabled: false,
			csv: {
				columnHeaderFormatter(item: { coll?: string } | null, key?: string) {
					if (!item || item.coll === 'xAxis') return columnLabel ?? fallbackLabels.column;
					if (key === 'y') return rowLabel ?? fallbackLabels.row;
					if (key === 'value') return valueLabel ?? fallbackLabels.value;
					return false;
				},
			},
		},
		legend: { enabled: false },
		colorAxis: { min: 0, max: maxValue, minColor, maxColor },
		xAxis: {
			categories: colKeys,
			opposite: true,
			lineColor: gridColor,
			tickColor: gridColor,
			title: columnLabel ? { text: columnLabel, style: axisLabelStyle } : { text: '' },
			labels: { useHTML: true, style: axisLabelStyle },
		},
		yAxis: {
			categories: rowKeys,
			reversed: true,
			title: rowLabel ? { text: rowLabel, style: axisLabelStyle } : { text: '' },
			labels: { useHTML: true, style: axisLabelStyle },
		},
		tooltip: {
			useHTML: true,
			outside: true,
			style: { zIndex: 9999 },
			formatter: function (this: Highcharts.Point & { value?: number | null }) {
				const { x = 0, y = 0, value } = this;
				const cell = value == null ? '\u2014' : formatValue(value, valueFormat);
				return `<b>${rowKeys[y]} \u00b7 ${colKeys[x]}</b><br/>${valueLabel ? `${valueLabel}: ` : ''}${cell}`;
			},
		},
		series: [
			{
				type: 'heatmap',
				borderWidth: 1,
				borderColor: gridColor,
				nullColor: 'transparent',
				data,
				...(onCellClick
					? {
							cursor: 'pointer',
							point: {
								events: {
									click: function (this: Highcharts.Point & { x?: number; y?: number }) {
										onCellClick(`${rowKeys[this.y ?? 0]} \u00d7 ${colKeys[this.x ?? 0]}`);
									},
								},
							},
						}
					: {}),
				dataLabels: {
					enabled: true,
					color: labelColor,
					style: { fontWeight: 'normal', fontSize: '11px', textOutline: 'none' },
					formatter: function (this: Highcharts.Point & { value?: number | null }) {
						return this.value == null ? '' : formatValue(this.value, valueFormat);
					},
				},
			},
		],
	};
}

/**
 * Renders the Data Agent's Matrix node — a 2-D categorical grid — as a
 * Highcharts heatmap. Cell shade scales with value magnitude; absent cells
 * render as 0 for count grids or blank otherwise.
 */
export function MatrixNode({ node }: BizyNodeProps) {
	const { payload } = node as BizyMatrixNode;
	const { title, rowLabel, columnLabel, valueLabel, valueFormat, zeroFill, rows, columns, cells, totalRows, selection } = payload;
	const chartRef = useRef<HighchartsReact.RefObject>(null);
	const { t } = useTranslation('admin_bizy');
	const { drillEnabled, drill, expand } = useEvidenceDrill();
	const [axisColor, gridColor, labelColor] = useToken('colors', ['text.muted', 'border.soft', 'text.default']).map(resolveCssColor);
	const heatMinColor = useColorModeValue(HEAT_MIN_LIGHT, HEAT_MIN_DARK);

	const rowKeys = Array.from(new Set(rows.filter((r) => r.trim().length > 0)));
	const colKeys = Array.from(new Set(columns.filter((c) => c.trim().length > 0)));
	if (rowKeys.length === 0 || colKeys.length === 0) return null;

	const byCell = new Map<string, number | undefined>();
	cells.forEach((cell) => byCell.set(cellKey(cell.row, cell.column), cell.value));
	const valueAt = (row: string, col: string) => byCell.get(cellKey(row, col));

	const fillZeros = zeroFill ?? valueFormat === 'number';

	const onDrill = drillEnabled ? (label: string) => drill('matrix', label, { title }) : undefined;
	const onExpand = drillEnabled && totalRows != null ? () => expand('matrix', totalRows as number, { title, noun: rowLabel }) : undefined;

	const options = buildHeatmapOptions({
		rowKeys,
		colKeys,
		valueAt,
		fillZeros,
		valueFormat,
		rowLabel,
		columnLabel,
		valueLabel,
		fallbackLabels: { row: t('chartFallback.row'), column: t('chartFallback.column'), value: t('chartFallback.value') },
		axisColor,
		gridColor,
		labelColor,
		minColor: heatMinColor,
		onCellClick: onDrill,
	});
	const height = rowKeys.length * 36 + 80;

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-matrix" sx={{ '&:hover .bizy-matrix-actions': { opacity: 1 } }}>
			{title ? (
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{title}
				</Text>
			) : null}
			<Box width="100%" sx={{ '.highcharts-container': { margin: '0 auto' } }}>
				<HighchartsReact ref={chartRef} highcharts={Highcharts} options={{ ...options, chart: { ...options.chart, height } }} />
			</Box>
			{totalRows != null ? <TruncationCaption shown={rowKeys.length} total={totalRows} selection={selection} onExpand={onExpand} /> : null}
			<HighchartsExportBar chartRef={chartRef} title={title} fallback="matrix" variant="heatmap" idPrefix="bizy-matrix" />
		</Flex>
	);
}
